<script setup lang="ts">
import {IconUICard, IconUICash, IconUILegalEntity, IconPaymentKaspi} from "#components";
import type {SelectPaymentMethodInterface} from "~/constants/values/payment/payment";
import {
  PAYMENT_CARD,
  PAYMENT_CARD_KEY,
  PAYMENT_CASH, PAYMENT_KASPI, PAYMENT_KASPI_KEY,
  PAYMENT_LEGAL_ENTITY,
  PAYMENT_LEGAL_ENTITY_KEY
} from "~/constants/values/payment/payment";
import DropdownWithCrud from "~/components/component/Dropdowns/DropdownWithCrud.vue";
import LegalEntityEditForm from "~/components/component/Order/Payment/LegalEntityEditForm.vue";
import LegalEntityForm from "~/components/component/Order/Payment/LegalEntityForm.vue";
import {deleteCard} from "~/composables/api/profile/card";
import {PAYMENT_INFO_CLIENTS_EMPLOYEE} from "~/constants/api/payment/payment";
import debounce from "lodash.debounce";

const emit = defineEmits(['selectPaymentMethod','updateCountCardsLength','selectLegalEntity'])
const props = defineProps({
  selectedPaymentMethod: {
    type: Object as () =>SelectPaymentMethodInterface,
    default: () => (<SelectPaymentMethodInterface>{
      method: PAYMENT_CARD,
      key: null,
      value: null
    })
  },
  isAuth: {
    type: Boolean,
    default: true
  }
})

const cards = ref([])
const legalEntities = ref([])
const {$toast, $toastError} = useNuxtApp()
const {t} = useI18n()
const selectedLegalEntity = ref<LegalEntitiesInterface | null>(null)
const selectedCard = ref<selectCardInterface|null>(null)
const showEditLegalEntity = ref(false)
const showCreateLegalEntity = ref(false)
const { profile } = useProfile()
//kaspi pay phone
const phone_number = ref(profile.value?.phone_number || '')
const showEditKaspiPhone = ref(false)
const kaspiPhoneErrorText = ref('')

function validateKaspiPhone(kaspiPhone: string) {
  let kaspiPhoneNoMask = removePhoneMask(kaspiPhone)
  if (kaspiPhoneNoMask.length < 1) {
    kaspiPhoneErrorText.value = t('validation.enterPhoneNumber')
  } else if (kaspiPhoneNoMask.length < 11) {
    kaspiPhoneErrorText.value = t('validation.validatePhoneNumber')
  } else {
    kaspiPhoneErrorText.value = ''
  }
}
function confirmKaspiPhone() {
  validateKaspiPhone(phone_number.value)
  if (kaspiPhoneErrorText.value) {
    return
  }
  showEditKaspiPhone.value = false
  selectPaymentType(PAYMENT_KASPI)
}

const variants = [
  {value: PAYMENT_KASPI, icon: IconPaymentKaspi},
  {value: PAYMENT_CARD, icon: IconUICard},
  {value: PAYMENT_LEGAL_ENTITY, icon: IconUILegalEntity},
  {value: PAYMENT_CASH, icon: IconUICash},
]
if (props.isAuth) {
  await fetchPaymentData()
  // await getCompanyEmployeeLegalEntity()
}

async function fetchPaymentData() {
  const standardLegalEntities = await getStandardPaymentData()
  const companyEmployeeLegalEntities = await getCompanyEmployeeLegalEntity()
  if (standardLegalEntities?.length)
    legalEntities.value.push(...standardLegalEntities)
  if (companyEmployeeLegalEntities?.length)
    legalEntities.value.push(...companyEmployeeLegalEntities)
}

async function getStandardPaymentData() {
  try {
    const {data: response, error, pending} = await getPaymentData()
    if (response.value) {
      const res = response.value as FetchPaymentData
      cards.value =  res.data.payment_cards
      // legalEntities.value = res.data.legal_entities
      emit('updateCountCardsLength', cards.value.length)
      return res.data.legal_entities
    }
  } catch (e) {
    console.log(e)
    return null
  }
}
async function getCompanyEmployeeLegalEntity() {
  try {
    const {data: response, error, pending} = await useApi(PAYMENT_INFO_CLIENTS_EMPLOYEE, 'GET')

    if (response.value) {
      const companies = response.value?.data
      let legalEntities = []
      companies.forEach(c => {
        c.legal_entities.forEach(e => legalEntities.push({
          ...e,
          title: `${e.name} ${formatPrice(c.remaining_sum)} тг`,
          show_controls: false,
          name: '',
          bin: ''
        }))
      })
      return legalEntities
    }
  } catch (e) {
    console.log(e)
    return null
  }
}
interface FetchPaymentData {
  data: {
    legal_entities: any;
    payment_cards: any;
  }
}


async function handleDeleteCard(item: selectCardInterface) {
  const {data: response, error, pending} = await deleteCard(item.subscription_id)
  if (response.value) {
    await fetchPaymentData()
    $toast(t('messages.success.delete'))
  } else {
    $toastError(t('some_problems.title'))
  }
}


function handleSelectCard(item:selectCardInterface)
{
  selectedCard.value = item
  emit('selectPaymentMethod', {
    method: PAYMENT_CARD,
    key: PAYMENT_CARD_KEY,
    value: item.subscription_id??null,
  })
}

function handleNewCard(){
  selectedCard.value = {
    subscription_id:'',
    account: t('order.other-card'),
    title:'other-card',
    type:'',
    options: {
      newCard: true,
    }
  }
  emit('selectPaymentMethod', {
    method: PAYMENT_CARD,
    key: PAYMENT_CARD_KEY,
    value: null,
    options: {
      newCard: true,
    }
  })
}

async function changedLegal() {
  handleCloseCreateEntity()
  handleCloseEditEntity()
  await fetchPaymentData()
  handleSelectLegalEntity(legalEntities.value[legalEntities.value.length-1]??null)
}

async function handleDeleteEntity(item: LegalEntitiesInterface) {
  const {data: response, error, pending} = await deleteLegalEntities(item.legal_entity_id)
  if (response.value) {
    if (selectedLegalEntity.value?.legal_entity_id===item.legal_entity_id ){
      handleSelectLegalEntity(null)
    }
    await fetchPaymentData()
    $toast(t('messages.success.delete'))
  } else {
    $toastError(t('some_problems.title'))
  }
}
function handleEditEntity(item: LegalEntitiesInterface) {
  selectedLegalEntity.value = item
  showEditLegalEntity.value = true
  showCreateLegalEntity.value = false
}

function handleCloseEditEntity() {
  showEditLegalEntity.value = false
}
function handleSelectLegalEntity(item: LegalEntitiesInterface|null) {
  emit('selectPaymentMethod', {
    method: PAYMENT_LEGAL_ENTITY,
    key: PAYMENT_LEGAL_ENTITY_KEY,
    value: item?.legal_entity_id
  })
  selectedLegalEntity.value = item
}
function handleSelectLegalEntityUnauth(item: LegalEntitiesInterface|null) {
  emit('selectLegalEntity', {
    method: PAYMENT_LEGAL_ENTITY,
    key: PAYMENT_LEGAL_ENTITY_KEY,
    value: {
      ...item,
      phone_number: item.phone_number.replace(/\D/g, '')
    }
  })
  selectedLegalEntity.value = item
}
function handleOpenCreateEntity() {
  handleCloseEditEntity()
  showCreateLegalEntity.value = true
}

function handleCloseCreateEntity() {
  showCreateLegalEntity.value = false
  handleSelectLegalEntity(legalEntities.value[0])
}
const selectPaymentType = debounce((paymentType:string) => {
  const payment = getPaymentKeyValue(paymentType)
  if (payment)
    emit('selectPaymentMethod', {
      method: paymentType,
      key: payment?.key,
      value: payment?.value,
      options:payment?.options
    })
}, 500)
function getPaymentKeyValue(paymentType:string): {key: string | null, value: string | null,options?:null|object } | null
{
  switch (paymentType) {
    case PAYMENT_CARD:
      return {
        key: PAYMENT_CARD_KEY,
        value: selectedCard.value?.subscription_id??null,
        options: {
          newCard: selectedCard.value?.options,
        }
      }
    case PAYMENT_LEGAL_ENTITY:
      return {
        key: PAYMENT_LEGAL_ENTITY_KEY,
        value: selectedLegalEntity.value?.legal_entity_id??null
      }
    case PAYMENT_CASH:
      return {
        key: null,
        value: null
      }
    case PAYMENT_KASPI:
      return {
        key: PAYMENT_KASPI_KEY,
        value: removePhoneMask(phone_number.value),
        options: {
          phone_number: removePhoneMask(phone_number.value)
        }
      }
    default:
      return null
  }
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <label
        v-for="variant in variants"
        :for="`payment_${variant.value}`"
        class="rounded-full transition duration-50 ease-in border md:text-lg text-sm font-normal flex md:px-6 px-2 items-center min-h-[42px]"
        :class="selectedPaymentMethod.method === variant.value ? 'border-primary-green bg-primary-green bg-opacity-5' : 'border-neutral-light-gray'"
    >
      <component v-if="variant.value === PAYMENT_KASPI" :is="variant.icon" class="md:mr-2"/>
      <span v-else>{{ $t(`order_product.payment_method.${variant.value}`) }}</span>

      <div class="md:py-2.5 py-2 flex text-center items-center ml-auto">
        <input
            type="radio"
            name="payment_method"
            :id="`payment_${variant.value}`"
            class="hidden"
            :value="variant.value"
            @click="selectPaymentType(variant.value)"
            v-model="selectedPaymentMethod.method"
        >
        <div
            v-if="variant.value === PAYMENT_KASPI"
            class="flex flex-col gap-1"
        >
          <div v-if="showEditKaspiPhone" class="flex gap-3 items-center">
            <client-only>
              <input
                  type="tel"
                  v-mask="'+7(###)###-##-##'"
                  v-model="phone_number"
                  @input="kaspiPhoneErrorText = ''"
                  class="w-[140px] text-base p-1 rounded-md"
              >
            </client-only>
            <button
                @click="confirmKaspiPhone"
                type="button"
                class="text-[#10B981] md:text-sm text-xs"
            >
              {{ $t('input_forms.button.confirm') }}
            </button>
          </div>
          <div v-else class="flex gap-3 items-center">
            <span class="text-sm">{{phone_number}}</span>
            <button
                @click="showEditKaspiPhone = true"
                type="button"
                class="text-[#10B981] md:text-sm text-xs"
            >
              {{ $t('input_forms.button.edit') }}
            </button>
          </div>
          <span
              v-if="kaspiPhoneErrorText"
              class="text-red-500 text-xs w-fit"
          >
              {{kaspiPhoneErrorText}}
            </span>
        </div>
      </div>
    </label>
  </div>
  <div v-if=" selectedPaymentMethod.method===PAYMENT_CARD && cards?.length>0">
    <p class="order-service-form-text mb-1"> {{ $t('order.select-card') }}</p>
    <DropdownWithCrud
      :list="cards"
      :key_field="PAYMENT_CARD_KEY"
      :title_field="['account']"
      :selected="selectedCard"
      :placeholder="$t('order.select-card')"
      :has-create="true"
      :has-delete="true"
      :create_text="$t('order.other-card')"
      @create="handleNewCard"
      @select="handleSelectCard"
      @delete="handleDeleteCard"
    />
  </div>
  <div v-if=" selectedPaymentMethod.method===PAYMENT_LEGAL_ENTITY ">
    <p class="order-service-form-text mb-1"> {{ $t('order.select-legal-entity') }}</p>
    <div v-if="!showCreateLegalEntity && legalEntities?.length>0">
      <DropdownWithCrud
          :list="legalEntities"
          :key_field="PAYMENT_LEGAL_ENTITY_KEY"
          :title_field="['name','bin', 'title']"
          :selected="selectedLegalEntity"
          :placeholder="t('order.select-legal-entity')"
          :has-create="true"
          :has-delete="true"
          :has-edit="true"
          @select="handleSelectLegalEntity"
          @delete="handleDeleteEntity"
          @edit="handleEditEntity"
          @create="handleOpenCreateEntity"
      />
      <div class="mt-1"
        v-if="showEditLegalEntity && selectedLegalEntity && selectedLegalEntity?.show_controls !== false"
        >
        <div  @click="handleCloseEditEntity" class="cursor-pointer">
          <-
        </div>
        <LegalEntityEditForm
            @changed="changedLegal"
            :legalEntity="selectedLegalEntity"
        />
      </div>
    </div>
    <div  class="mt-1">
      <div v-if="showCreateLegalEntity" @click="handleCloseCreateEntity" class="cursor-pointer">
        <-
      </div>
      <LegalEntityForm
          v-if="legalEntities?.length===0 || showCreateLegalEntity"
          :isAuth
          :legalEntitySelected="selectedPaymentMethod.value && selectedPaymentMethod.method === PAYMENT_LEGAL_ENTITY"
          @changed="changedLegal"
          @setValue="handleSelectLegalEntityUnauth"
      />
    </div>
  </div>
</template>

<style scoped>

</style>